import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router";
import { useDelete, useGet } from "../../api";
import {
  IAffiliateAdvertiser,
  IAdvertiserCategories,
  IProductsResponse,
  IAffiliateProduct,
} from "../../interfaces";
import {
  Button,
  Spinner,
  Badge,
  Table,
  Card,
  Row,
  Col,
  Form,
  OverlayTrigger,
  Tooltip,
  Alert,
} from "react-bootstrap";
import {
  ArrowLeft,
  ExternalLink,
  Copy,
  Check,
  RefreshCw,
  Search,
  Plus,
  Edit2,
  Trash2,
  ShoppingBag,
} from "react-feather";
import {
  ManualAdvertiserModal,
  ManualProductModal,
  ProductUrlModal,
  useDeleteManualAdvertiser,
  useDeleteManualProduct,
} from "./manual-components";

const CopyButton: React.FC<{ text: string; title?: string }> = ({
  text,
  title,
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{copied ? "Copied!" : title || "Copy"}</Tooltip>}
    >
      <button
        type="button"
        className="btn btn-link btn-sm p-0 ms-1"
        style={{ lineHeight: 1, verticalAlign: "middle" }}
        onClick={handleCopy}
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </OverlayTrigger>
  );
};

export const AdvertiserDetail: React.FC = () => {
  const navigate = useNavigate();
  const { network, advertiserId } = useParams<{
    network: string;
    advertiserId: string;
  }>();
  const deleteAdvertiser = useDeleteManualAdvertiser();
  const deleteProduct = useDeleteManualProduct();
  const [showAdvertiserModal, setShowAdvertiserModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [editingProduct, setEditingProduct] =
    useState<IAffiliateProduct | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Advertiser info
  const advertiser = useGet<IAffiliateAdvertiser>(
    network && advertiserId
      ? `affiliate-products/advertisers/${network}/${advertiserId}`
      : null
  );

  // Categories
  const categories = useGet<IAdvertiserCategories>(
    network && advertiserId
      ? `affiliate-products/categories?network=${network}&advertiserId=${advertiserId}`
      : null
  );

  const categoryList = useMemo(() => {
    return categories.data?.categories ?? [];
  }, [categories.data]);

  const [categorySearch, setCategorySearch] = useState("");

  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return categoryList;
    const q = categorySearch.toLowerCase();
    return categoryList.filter((c) => c.category.toLowerCase().includes(q));
  }, [categoryList, categorySearch]);

  // Products with pagination
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState("50");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");

  const productsUrl = useMemo(() => {
    if (!network || !advertiserId) return null;
    const params = new URLSearchParams();
    params.set("network", network);
    params.set("advertiserId", advertiserId);
    params.set("offset", String(offset));
    params.set("limit", limit);
    if (categoryFilter) params.set("category", categoryFilter);
    if (searchFilter) params.set("q", searchFilter);
    return `affiliate-products/products?${params.toString()}`;
  }, [network, advertiserId, offset, limit, categoryFilter, searchFilter]);

  const products = useGet<IProductsResponse>(productsUrl, !!productsUrl);
  const productsList = products.data?.products ?? [];
  const totalCount = products.data?.totalCount ?? 0;
  const pageSize = Number(limit);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.floor(offset / pageSize) + 1;
  const [pageInput, setPageInput] = useState("1");

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const jumpToPage = () => {
    const parsedPage = Number.parseInt(pageInput, 10);
    if (Number.isNaN(parsedPage)) {
      setPageInput(String(currentPage));
      return;
    }

    const clampedPage = Math.min(totalPages, Math.max(1, parsedPage));
    setOffset((clampedPage - 1) * pageSize);
  };

  const adv = advertiser.data;
  const isManual = adv?.affiliateNetwork === "Manual";

  const handleDeleteAdvertiser = async () => {
    if (!adv) return;
    if (
      !window.confirm(`Delete advertiser "${adv.name}"? This cannot be undone.`)
    ) {
      return;
    }
    setActionError(null);
    try {
      await deleteAdvertiser.execute(
        `affiliate-products/advertisers/Manual/${adv.advertiserId}`
      );
      navigate("/affiliate-products/discovery");
    } catch (err: any) {
      setActionError(
        err?.data?.error ||
          err?.data?.message ||
          err?.message ||
          "Unknown error"
      );
    }
  };

  const handleDeleteProduct = async (product: IAffiliateProduct) => {
    if (!adv) return;
    if (
      !window.confirm(
        `Delete product "${product.name}"? This cannot be undone.`
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      await deleteProduct.execute(
        `affiliate-products/products/${adv.advertiserId}/${encodeURIComponent(product.id)}`
      );
      await products.refetch();
      await advertiser.refetch();
    } catch (err: any) {
      setActionError(
        err?.data?.error ||
          err?.data?.message ||
          err?.message ||
          "Unknown error"
      );
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <Link
          to="/affiliate-products/discovery"
          className="btn btn-outline-secondary btn-sm me-3"
        >
          <ArrowLeft size={14} className="me-1" />
          Back
        </Link>
        <h4 className="mb-0">
          {adv?.name || advertiserId}
          {adv && (
            <Badge bg="dark" className="ms-2" style={{ fontSize: "0.7rem" }}>
              {adv.affiliateNetwork}
            </Badge>
          )}
        </h4>
        {isManual && adv && (
          <div className="ms-auto d-flex gap-2">
            <Button
              size="sm"
              variant="success"
              onClick={() => {
                setEditingProduct(null);
                setShowProductModal(true);
              }}
            >
              <Plus size={12} className="me-1" />
              Add Product
            </Button>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => setShowUrlModal(true)}
            >
              <Plus size={12} className="me-1" />
              Import from URL
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setShowAdvertiserModal(true)}
            >
              <Edit2 size={12} className="me-1" />
              Edit Advertiser
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={handleDeleteAdvertiser}
            >
              <Trash2 size={12} className="me-1" />
              Delete Advertiser
            </Button>
          </div>
        )}
      </div>

      {actionError && <Alert variant="danger">{actionError}</Alert>}

      {advertiser.loading && (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" /> Loading advertiser...
        </div>
      )}

      {advertiser.error && (
        <div className="text-danger mb-3">
          Error: {advertiser.error.message}
        </div>
      )}

      {/* Advertiser Info Card */}
      {adv && (
        <Card className="mb-3">
          <Card.Body>
            <Row>
              <Col md={3}>
                <small className="text-muted d-block">Advertiser ID</small>
                <strong>{adv.advertiserId}</strong>
                <CopyButton text={adv.advertiserId} />
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Domain</small>
                {adv.domain ? (
                  <>
                    {adv.domain}
                    {adv.websiteUrl && (
                      <a
                        href={adv.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ms-1"
                      >
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </Col>
              <Col md={2}>
                <small className="text-muted d-block">Products</small>
                <strong>{adv.productCount.toLocaleString()}</strong>
              </Col>
              <Col md={2}>
                <small className="text-muted d-block">Region</small>
                {adv.region || "—"}
              </Col>
              <Col md={2}>
                <small className="text-muted d-block">Currency</small>
                {adv.currency || "—"}
              </Col>
            </Row>
            {adv.description && (
              <Row className="mt-2">
                <Col>
                  <small className="text-muted d-block">Description</small>
                  <small>{adv.description}</small>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Categories Section */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>
            Categories{" "}
            {categoryList.length > 0 && (
              <Badge bg="secondary" className="ms-1">
                {filteredCategories.length}
                {filteredCategories.length !== categoryList.length &&
                  ` / ${categoryList.length}`}
              </Badge>
            )}
          </strong>
          <div className="d-flex gap-2 align-items-center">
            <Form.Control
              size="sm"
              placeholder="Filter categories..."
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              style={{ width: 200 }}
            />
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => categories.refetch()}
              disabled={categories.loading}
            >
              <RefreshCw size={12} />
            </Button>
          </div>
        </Card.Header>
        <div style={{ maxHeight: 300, overflowY: "auto" }}>
          {categories.loading && (
            <div className="text-center py-2">
              <Spinner animation="border" size="sm" />
            </div>
          )}
          {categories.error && (
            <div className="text-danger p-3">
              Error: {categories.error.message}
            </div>
          )}
          {!categories.loading && categoryList.length === 0 && (
            <div className="text-muted text-center py-3">
              No categories found for this advertiser.
            </div>
          )}
          {filteredCategories.length > 0 && (
            <Table
              striped
              hover
              size="sm"
              className="mb-0"
              style={{ fontSize: "0.8rem" }}
            >
              <thead>
                <tr>
                  <th>Category</th>
                  <th style={{ width: 120 }}>Products</th>
                  <th style={{ width: 160 }}>Last Updated</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories.map((cat) => (
                  <tr key={cat.category}>
                    <td>{cat.category}</td>
                    <td>{cat.productCount.toLocaleString()}</td>
                    <td>
                      {cat.lastUpdatedAt ? (
                        <small>
                          {new Date(cat.lastUpdatedAt).toLocaleDateString()}
                        </small>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0"
                        title="Filter products by this category"
                        onClick={() => {
                          setCategoryFilter(cat.category);
                          setOffset(0);
                        }}
                      >
                        <Search size={12} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>

      {/* Products Section */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <strong>
              Products{" "}
              {totalCount > 0 && (
                <Badge bg="secondary" className="ms-1">
                  {totalCount.toLocaleString()}
                </Badge>
              )}
            </strong>
            <div className="d-flex gap-2">
              {isManual && adv && (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    setEditingProduct(null);
                    setShowProductModal(true);
                  }}
                >
                  <Plus size={12} className="me-1" />
                  Add Product
                </Button>
              )}
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => products.refetch()}
                disabled={products.loading}
              >
                <RefreshCw size={12} />
              </Button>
            </div>
          </div>
          <Row className="g-2 mt-2">
            <Col md={3}>
              <Form.Control
                size="sm"
                placeholder="Search products..."
                value={searchFilter}
                onChange={(e) => {
                  setSearchFilter(e.target.value);
                  setOffset(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") products.refetch();
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Control
                size="sm"
                placeholder="Filter by category..."
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setOffset(0);
                }}
              />
            </Col>
            <Col md={2}>
              <Form.Select
                size="sm"
                value={limit}
                onChange={(e) => {
                  setLimit(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Form.Select>
            </Col>
            <Col md={4} className="d-flex justify-content-end gap-2">
              <Form.Control
                size="sm"
                type="number"
                min={1}
                max={totalPages}
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") jumpToPage();
                }}
                style={{ width: 90 }}
                aria-label="Page number"
              />
              <Button size="sm" variant="outline-primary" onClick={jumpToPage}>
                Go
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - pageSize))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={currentPage >= totalPages}
                onClick={() => setOffset(offset + pageSize)}
              >
                Next
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body style={{ padding: 0 }}>
          {products.loading && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" /> Loading products...
            </div>
          )}
          {products.error && (
            <div className="text-danger p-3">
              Error: {products.error.message}
            </div>
          )}
          {!products.loading && productsList.length === 0 && (
            <div className="text-muted text-center py-3">
              No products found.
            </div>
          )}
          {productsList.length > 0 && (
            <>
              <small className="text-muted d-block px-3 pt-2">
                Showing {productsList.length} of {totalCount.toLocaleString()}
                {totalCount > 0 && ` (page ${currentPage} of ${totalPages})`}
              </small>
              <Table
                striped
                bordered
                hover
                size="sm"
                className="mb-0"
                style={{ fontSize: "0.8rem" }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "30%" }}>Product</th>
                    <th>Manufacturer</th>
                    <th>Categories</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Sale</th>
                    <th>Links</th>
                    {isManual && <th style={{ width: 90 }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {productsList.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="d-flex align-items-start">
                          {p.thumbnailUrl && (
                            <img
                              src={p.thumbnailUrl}
                              alt=""
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: "cover",
                                marginRight: 8,
                                borderRadius: 4,
                              }}
                            />
                          )}
                          <div>
                            <strong>{p.name}</strong>
                            <CopyButton text={p.id} title="Copy Product ID" />
                            {p.sku && (
                              <div
                                className="text-muted"
                                style={{ fontSize: "0.75rem" }}
                              >
                                SKU: {p.sku}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{p.manufacturer || "—"}</td>
                      <td>
                        {p.categories.slice(0, 2).map((c, i) => (
                          <Badge
                            key={i}
                            bg="light"
                            text="dark"
                            className="me-1 mb-1"
                          >
                            {c}
                          </Badge>
                        ))}
                        {p.categories.length > 2 && (
                          <Badge bg="light" text="dark">
                            +{p.categories.length - 2}
                          </Badge>
                        )}
                      </td>
                      <td>
                        <strong>
                          {p.currency || ""}
                          {p.price?.toFixed(2)}
                        </strong>
                        {p.salePrice != null && (
                          <div
                            className="text-success"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Sale: {p.currency || ""}
                            {p.salePrice.toFixed(2)}
                          </div>
                        )}
                      </td>
                      <td>
                        {p.inStock ? (
                          <Badge bg="success">In Stock</Badge>
                        ) : (
                          <Badge bg="danger">Out</Badge>
                        )}
                      </td>
                      <td>
                        {p.isOnSale ? (
                          <Badge bg="warning" text="dark">
                            Sale
                          </Badge>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {p.productUrl && (
                          <a
                            href={p.productUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="me-2"
                            title="Affiliate link — goes through the network's tracker"
                          >
                            <ExternalLink size={12} />
                          </a>
                        )}
                        {p.merchantUrl && (
                          <a
                            href={p.merchantUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Merchant product page — direct, untracked"
                          >
                            <ShoppingBag size={12} />
                          </a>
                        )}
                      </td>
                      {isManual && (
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => {
                                setEditingProduct(p);
                                setShowProductModal(true);
                              }}
                            >
                              <Edit2 size={11} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDeleteProduct(p)}
                            >
                              <Trash2 size={11} />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Card.Body>
      </Card>

      {isManual && adv && (
        <>
          <ManualAdvertiserModal
            show={showAdvertiserModal}
            advertiser={adv}
            onHide={() => setShowAdvertiserModal(false)}
            onSaved={async () => {
              await advertiser.refetch();
            }}
          />
          <ManualProductModal
            show={showProductModal}
            advertiser={adv}
            product={editingProduct}
            onHide={() => setShowProductModal(false)}
            onSaved={async () => {
              await products.refetch();
              await advertiser.refetch();
            }}
          />
          <ProductUrlModal
            show={showUrlModal}
            advertiser={adv}
            onHide={() => setShowUrlModal(false)}
            onSaved={async () => {
              await products.refetch();
              await advertiser.refetch();
            }}
          />
        </>
      )}
    </div>
  );
};
